import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  Typography,
  useMediaQuery,
  type Theme
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDataProvider } from "react-admin";
import { useRollbar } from "@rollbar/react";
import { COLORS } from "../../common/colors";
import type {
  PatientRow,
  PatientSidebar
} from "src/providers/compliance/complianceTypes";
import {
  STREAM_DISPLAY,
  TRIAGE_LABELS
} from "src/providers/compliance/complianceTypes";
import type {
  ComplianceFilters,
  ComplianceView
} from "src/providers/compliance/complianceFilters";
import {
  isNewPatient,
  isExempt,
  MED_BASIS_LABELS,
  DEFAULT_TD_THRESHOLD_HOURS,
  patientToday
} from "src/providers/compliance/complianceFilters";
import { dateDaysBefore } from "src/providers/compliance/complianceClock";
import {
  adherenceSummary,
  sectionErrorText,
  medicationSeriesForBasis,
  sumMedicationSeries
} from "src/providers/compliance/complianceSidebar";
import {
  DailySummaryError,
  SIDEBAR_LOAD_FAILED
} from "src/providers/compliance/complianceSidebarApi";
import { CB, COMPLIANCE_COLORS, triageGroupColors } from "./complianceColors";
import { MedicationBarCharts } from "./MedicationBarCharts";
import { DailyStreamChart } from "./DailyStreamChart";
import { OutreachTimeline } from "./OutreachTimeline";
import { useCategoryMetadata } from "src/providers/CategoryMetadataContext";
import type { MedicationOption } from "src/providers/categoryMetadata";

interface PatientSidebarDrawerProps {
  patient: PatientRow | null;
  view: ComplianceView;
  filters: ComplianceFilters;
  now: Date;
  orgId: string;
  projectId: string;
  onClose: () => void;
}

// Two weeks shows a 'last Tuesday' pattern without crowding the sidebar.
const WINDOW_DAYS = 14;

const SIDEBAR_WIDTH = 460;
function formatFullDate(dateStr: string): string {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric"
  });
}

function DetailField({
  label,
  value
}: {
  label: string;
  value: string;
}): JSX.Element {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 600,
          fontFamily: "Work Sans, sans-serif",
          color: COLORS.GREY_800
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 500,
          fontFamily: "Work Sans, sans-serif",
          color: COLORS.GREY_900
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function SectionHeader({ label }: { label: string }): JSX.Element {
  return (
    <Typography
      sx={{
        fontSize: 18,
        fontWeight: 600,
        fontFamily: "Work Sans, sans-serif",
        color: COLORS.GREY_900,
        mb: 1
      }}
    >
      {label}
    </Typography>
  );
}

function SidebarSection({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <Box
      sx={{
        py: 2,
        borderBottom: `1px solid ${COMPLIANCE_COLORS.BORDER_SECTION}`
      }}
    >
      <SectionHeader label={label} />
      {children}
    </Box>
  );
}

function SectionMessage({
  children,
  color
}: {
  children: React.ReactNode;
  color?: string;
}): JSX.Element {
  return (
    <Typography
      sx={{
        fontSize: 12,
        fontFamily: "Work Sans, sans-serif",
        color: color ?? COLORS.GREY_800
      }}
    >
      {children}
    </Typography>
  );
}

interface MedicationSectionProps {
  medBasisLabel: string;
  sidebar: PatientSidebar;
  view: ComplianceView;
  dates: string[];
  todayDate: string;
  medicationOptions: MedicationOption[];
}

function MedicationSection({
  medBasisLabel,
  sidebar,
  view,
  dates,
  todayDate,
  medicationOptions
}: MedicationSectionProps): JSX.Element {
  const errorMsg = sidebar.errors.medication;
  if (errorMsg) {
    return (
      <SidebarSection label={medBasisLabel}>
        <SectionMessage color={CB.VERMILLION_TEXT}>
          {sectionErrorText(errorMsg, "medication")}
        </SectionMessage>
      </SidebarSection>
    );
  }

  const medSeries = medicationSeriesForBasis(
    sidebar.medicationDays,
    view.medBasis,
    view.projectConfig.studyMedications,
    dates,
    todayDate
  );
  const medDays = sumMedicationSeries(medSeries, dates, todayDate);

  return (
    <SidebarSection label={medBasisLabel}>
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 500,
          fontStyle: "italic",
          fontFamily: "Work Sans, sans-serif",
          color: COLORS.GREY_900,
          mb: 1
        }}
      >
        {adherenceSummary(medDays, view.medBasis, dates.length)}
      </Typography>
      <MedicationBarCharts
        series={medSeries}
        dates={dates}
        todayDate={todayDate}
        studyMedications={view.projectConfig.studyMedications}
        catalog={medicationOptions}
      />
    </SidebarSection>
  );
}

interface StreamSectionProps {
  displayName: string;
  sidebar: PatientSidebar;
  streamName: string;
  dates: string[];
  mode: "bar" | "presence";
  threshold: number;
  todayDate: string;
}

export function StreamSection({
  displayName,
  sidebar,
  streamName,
  dates,
  mode,
  threshold,
  todayDate
}: StreamSectionProps): JSX.Element {
  const errorMsg = sidebar.errors[streamName as keyof typeof sidebar.errors];
  if (errorMsg) {
    return (
      <SidebarSection label={displayName}>
        <SectionMessage color={CB.VERMILLION_TEXT}>
          {sectionErrorText(errorMsg, displayName)}
        </SectionMessage>
      </SidebarSection>
    );
  }

  const days =
    sidebar.streams[streamName as keyof typeof sidebar.streams] ?? [];

  // A stream the patient has lists every date, so an empty one means they have no such
  // stream. Bowel movements are logged in the app rather than streamed, so always list dates.
  if (days.length === 0 && streamName !== "bowel_movement") {
    return (
      <SidebarSection label={displayName}>
        <SectionMessage>No data stream for this patient</SectionMessage>
      </SidebarSection>
    );
  }

  return (
    <SidebarSection label={displayName}>
      <DailyStreamChart
        days={days}
        dates={dates}
        mode={mode}
        threshold={threshold}
        todayDate={todayDate}
      />
    </SidebarSection>
  );
}

export function PatientSidebarDrawer({
  patient,
  view,
  filters,
  now,
  orgId,
  projectId,
  onClose
}: PatientSidebarDrawerProps): JSX.Element {
  const dataProvider = useDataProvider();
  const rollbar = useRollbar();
  const [sidebar, setSidebar] = useState<PatientSidebar | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { medicationOptions } = useCategoryMetadata();
  const lastPatientRef = useRef<PatientRow | null>(null);

  if (patient !== null) {
    lastPatientRef.current = patient;
  }

  const displayPatient = patient ?? lastPatientRef.current;

  // Keyed on the patient and window, not the row object: re-triage on any filter change makes a
  // new row, which would otherwise refetch the open sidebar.
  const patientId = patient?.patientId ?? null;
  const endDate = patient ? patientToday(patient, now) : null;
  const zoneLoaded = patient?.proLoaded ?? false;

  useEffect(() => {
    // Closing keeps the last content so the drawer doesn't empty while it slides out.
    if (patientId === null || endDate === null) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    // Without the patient's time zone, endDate is the UTC date, which can be a day off their
    // local one. Start a day early and let Tangerine end on their local today, so the days
    // shown (counted back from the response's end date) are covered either way.
    const startDate = dateDaysBefore(
      endDate,
      zoneLoaded ? WINDOW_DAYS - 1 : WINDOW_DAYS
    );

    dataProvider
      .getPatientSidebar({
        patientId,
        orgId,
        projectId,
        startDate,
        ...(zoneLoaded ? { endDate } : {})
      })
      .then((result: PatientSidebar) => {
        if (!cancelled) {
          setSidebar(result);
          setLoading(false);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          const known = err instanceof DailySummaryError;
          if (!known || !err.expected) {
            rollbar.error("Patient sidebar load failed", err as Error);
          }
          setError(known ? err.message : SIDEBAR_LOAD_FAILED);
          setSidebar(null);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [patientId, endDate, zoneLoaded, orgId, projectId, dataProvider, rollbar]);

  const open = patient !== null;
  const triageColors = displayPatient
    ? triageGroupColors(displayPatient.triage)
    : null;

  // Too narrow to sit beside the list, so it overlays it as a modal (which handles focus and
  // Escape itself).
  const overlay = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));
  const paperRef = useRef<HTMLDivElement>(null);

  // Beside the list nothing moves focus for us: take it on open or patient switch so keyboard
  // and screen reader users land in the sidebar.
  useEffect(() => {
    if (overlay || patientId === null) return;
    const frame = requestAnimationFrame(() => paperRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [overlay, patientId]);

  const lastPatientId = displayPatient?.patientId ?? null;
  const close = useCallback(() => {
    // Back to the patient's row, so closing doesn't lose the user's place in the list.
    if (!overlay && paperRef.current?.contains(document.activeElement)) {
      document
        .querySelector<HTMLElement>(
          `[data-patient-row="${CSS.escape(lastPatientId ?? "")}"]`
        )
        ?.focus();
    }
    onClose();
  }, [overlay, lastPatientId, onClose]);

  useEffect(() => {
    if (overlay || !open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      // Escape in a text field clears or leaves the field; it shouldn't also close the sidebar.
      const target = e.target as HTMLElement | null;
      if (target?.closest("input, textarea, select, [contenteditable='true']"))
        return;
      close();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [overlay, open, close]);

  const paperSx = {
    width: SIDEBAR_WIDTH,
    borderLeft: `2px solid ${COLORS.GREY_400}`
  };

  // Beside the list (persistent, in the page's flex row) the list stays scrollable and clickable
  // and shrinks instead of hiding rows underneath; clicks on the panel never reach the list.
  return (
    <Drawer
      anchor="right"
      variant={overlay ? "temporary" : "persistent"}
      open={open}
      onClose={close}
      sx={
        overlay
          ? undefined
          : {
              width: open ? SIDEBAR_WIDTH : 0,
              flexShrink: 0,
              overflow: "hidden",
              transition: (theme) =>
                theme.transitions.create("width", {
                  duration: open
                    ? theme.transitions.duration.enteringScreen
                    : theme.transitions.duration.leavingScreen
                })
            }
      }
      PaperProps={{
        ref: paperRef,
        tabIndex: -1,
        role: overlay ? undefined : "complementary",
        "aria-label": "Patient sidebar",
        sx: overlay
          ? { ...paperSx, maxWidth: "90vw" }
          : {
              ...paperSx,
              position: "relative",
              height: "100%",
              outline: "none"
            }
      }}
    >
      {loading && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%"
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mb: 1
            }}
          >
            <IconButton size="small" onClick={close} aria-label="Close">
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Box>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 500,
              fontFamily: "Work Sans, sans-serif",
              color: CB.VERMILLION_TEXT,
              textAlign: "center",
              py: 4
            }}
          >
            {error}
          </Typography>
        </Box>
      )}

      {!loading &&
        !error &&
        sidebar &&
        displayPatient &&
        triageColors &&
        (() => {
          const dates = Array.from({ length: WINDOW_DAYS }, (_, i) =>
            dateDaysBefore(sidebar.endDate, WINDOW_DAYS - 1 - i)
          );
          const todayDate = dates[dates.length - 1];

          return (
            <Box
              sx={{ display: "flex", flexDirection: "column", height: "100%" }}
            >
              {/* Sticky header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2.5,
                  py: 2,
                  borderBottom: `1px solid ${COMPLIANCE_COLORS.BORDER_SECTION}`,
                  position: "sticky",
                  top: 0,
                  backgroundColor: "#ffffff",
                  zIndex: 1
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 18,
                      fontWeight: 600,
                      fontFamily: "Work Sans, sans-serif",
                      color: COLORS.GREY_900
                    }}
                  >
                    {displayPatient.codeName}
                  </Typography>
                  {isNewPatient(displayPatient, now) && (
                    <Box
                      sx={{
                        px: 1,
                        py: 0.25,
                        borderRadius: "4px",
                        backgroundColor: CB.SKY_BG,
                        color: CB.SKY_TEXT,
                        fontSize: 11,
                        fontWeight: 600,
                        fontFamily: "Work Sans, sans-serif"
                      }}
                    >
                      NEW
                    </Box>
                  )}
                </Box>
                <IconButton size="small" onClick={close} aria-label="Close">
                  <CloseIcon sx={{ fontSize: 20 }} />
                </IconButton>
              </Box>

              {/* Scrollable content */}
              <Box sx={{ flex: 1, overflow: "auto", px: 2.5, py: 2 }}>
                {/* Triage pill and metadata */}
                <Box
                  sx={{
                    pb: 2,
                    borderBottom: `1px solid ${COMPLIANCE_COLORS.BORDER_SECTION}`
                  }}
                >
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "999px",
                      backgroundColor: triageColors.bg,
                      color: triageColors.fg
                    }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        backgroundColor: triageColors.dot
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 600,
                        fontFamily: "Work Sans, sans-serif"
                      }}
                    >
                      {TRIAGE_LABELS[displayPatient.triage]}
                    </Typography>
                  </Box>
                  {displayPatient.triageReasons.map((reason) => (
                    <Typography
                      key={reason}
                      sx={{
                        fontSize: 11,
                        fontFamily: "Work Sans, sans-serif",
                        color: COLORS.GREY_800,
                        mt: 0.5
                      }}
                    >
                      {reason}
                    </Typography>
                  ))}
                  {sidebar.timezoneMissing && (
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontFamily: "Work Sans, sans-serif",
                        color: CB.ORANGE_TEXT,
                        mt: 1
                      }}
                    >
                      No time zone, days counted in UTC
                    </Typography>
                  )}
                  <Box sx={{ display: "flex", gap: 4, mt: 1.5 }}>
                    <DetailField
                      label="End date"
                      value={formatFullDate(sidebar.endDate)}
                    />
                    <DetailField
                      label="Created at"
                      value={formatFullDate(displayPatient.createdAt)}
                    />
                  </Box>
                </Box>

                {view.columns.map((streamName) => {
                  const displayName =
                    STREAM_DISPLAY[streamName]?.long || streamName;

                  if (streamName === "medication") {
                    const exempt = isExempt(displayPatient, "medication");
                    const medBasisLabel = MED_BASIS_LABELS[view.medBasis];

                    if (exempt) {
                      return (
                        <SidebarSection key={streamName} label={medBasisLabel}>
                          <SectionMessage>Exempt</SectionMessage>
                        </SidebarSection>
                      );
                    }

                    return (
                      <MedicationSection
                        key={streamName}
                        medBasisLabel={medBasisLabel}
                        sidebar={sidebar}
                        view={view}
                        dates={dates}
                        todayDate={todayDate}
                        medicationOptions={medicationOptions}
                      />
                    );
                  }

                  const exempt = isExempt(displayPatient, streamName);
                  if (exempt) {
                    return (
                      <SidebarSection key={streamName} label={displayName}>
                        <SectionMessage>Exempt</SectionMessage>
                      </SidebarSection>
                    );
                  }

                  const mode =
                    streamName === "tremor_dyskinesia" ? "bar" : "presence";
                  const threshold =
                    filters.tdThresholdHours ?? DEFAULT_TD_THRESHOLD_HOURS;

                  return (
                    <StreamSection
                      key={streamName}
                      displayName={displayName}
                      sidebar={sidebar}
                      streamName={streamName}
                      dates={dates}
                      mode={mode}
                      threshold={threshold}
                      todayDate={todayDate}
                    />
                  );
                })}

                <Box sx={{ py: 2 }}>
                  <SectionHeader label="Outreach" />
                  <OutreachTimeline
                    events={sidebar.outreach}
                    dates={dates}
                    lastContactDays={displayPatient.contact?.gapDays ?? null}
                    timezone={sidebar.timezone}
                  />
                </Box>
              </Box>
            </Box>
          );
        })()}
    </Drawer>
  );
}
